import { AccentVariantEnum } from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { Card } from '@delmonicos/shared-design-system-react/src/components/card/card';
import { Icon } from '@delmonicos/shared-design-system-react/src/components/icon/icon';
import { Link } from '@delmonicos/shared-design-system-react/src/components/link/link';
import { GeoMap } from '@delmonicos/shared-design-system-react/src/components/location/geo-map/geo-map.component';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import {
  Text,
  TextColor,
} from '@delmonicos/shared-design-system-react/src/components/typography/text/text';
import { AccentTitle } from '@delmonicos/shared-design-system-react/src/components/typography/title/accent-title/accent-title';
import { HeadingEnum } from '@delmonicos/shared-design-system-react/src/models/enums/heading.enum';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import { ConfigurationService } from '@app/core/configuration/configuration.service';
import { useLocationContext } from '@app/feature/location/location.context';

import * as css from './localization-card.module.scss';

export const LocalizationCardComponent = (): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocationContext();

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleActionRead = () => {
    navigate(
      generatePath(APP_PATH.NETWORK.DETAILS, {
        id: location.network.id,
      }),
      {
        replace: false,
      },
    );
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <Card flex={1} gap={GapEnum.L} direction="column">
      <AccentTitle
        as={HeadingEnum.H2}
        icon="marker-pin-04"
        text={t('$.location-details.localization.title')}
        variant={AccentVariantEnum.Secondary}
      />
      <Stack gap={GapEnum.M} direction="column">
        <Stack gap={GapEnum.S} justifyContent="space-between">
          <Text bold>
            <Icon name="server-06" />
            {t('delmonicos-shared-sdk.outputs-location.network')}
          </Text>
          <Link onClick={handleActionRead}>{location.network?.name}</Link>
        </Stack>
        <Stack
          gap={GapEnum.S}
          alignItems="center"
          justifyContent="space-between">
          <Text bold>
            <Icon name="marker-pin-01" />
            {t('delmonicos-shared-sdk.outputs-location.full-address')}
          </Text>
          <Stack direction="column" alignItems="flex-end">
            <Text>{`${location.address},`}</Text>
            <Text>
              {`${location.postalCode} ${location.city} - ${location.country}`}
            </Text>
          </Stack>
        </Stack>
        <Stack
          gap={GapEnum.XL}
          alignItems="center"
          justifyContent="space-between">
          <Stack
            justifyContent="space-between"
            gap={GapEnum.S}
            alignItems="center"
            flex={1}>
            <Stack gap={GapEnum.S} alignItems="center">
              <Icon name="marker-pin-04" />
              <Text bold>
                {t('delmonicos-shared-sdk.outputs-geo-location.latitude')}
              </Text>
            </Stack>
            <Text>{location.coordinates.latitude}</Text>
          </Stack>
          <Stack
            justifyContent="space-between"
            gap={GapEnum.S}
            alignItems="center"
            flex={1}>
            <Stack gap={GapEnum.S} alignItems="center">
              <Icon name="marker-pin-04" />
              <Text bold>
                {t('delmonicos-shared-sdk.outputs-geo-location.longitude')}
              </Text>
            </Stack>
            <Text>{location.coordinates.longitude}</Text>
          </Stack>
        </Stack>
      </Stack>
      <Stack flex={1} className={css.mapContainer}>
        {ConfigurationService.getGoogleApiKey() ? (
          <GeoMap
            mapId="static-map-location-detail"
            apiKey={ConfigurationService.getGoogleApiKey()}
            zoom={16}
            position={{
              lat: Number(location.coordinates.latitude),
              lng: Number(location.coordinates.longitude),
            }}
          />
        ) : (
          <Text color={TextColor.Error}>
            {t('global.missing-google-api-key')}
          </Text>
        )}
      </Stack>
    </Card>
  );
};
